import {
  cn,
  KdsButton,
  KdsButtonIcon,
  KdsFocusableElement,
  KdsInput,
  KdsText,
  KdsTooltip,
} from "@kazaar-platform/design-system";
import {
  RiAddCircleFill,
  RiEditLine,
  RiExpandDiagonal2Line,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import { MAX_VERSION_PER_ADDRESS_QUANTITY_LENGHT } from "@/constants/orderItemQuantites.constants";
import { getUniqueAddressesFromOrderItemConfigurationForm } from "@/helpers/getUniqueAddressesFromOrderItemConfigurationForm";
import { useIsHorizontallyScrollable } from "@/hooks/useIsHorizontallyScrollable";
import { type OrderItemConfigTableVersionsProps } from "./OrderItemConfigTableVersions.types";

export const OrderItemConfigTableVersions = ({
  isModificationRequest = false,
  onExpand,
  onPressEditVersion,
  className,
}: OrderItemConfigTableVersionsProps) => {
  const { t } = useTranslate();

  const classNames = cn("flex w-full", className);

  const methods = useFormContext<OrderItemConfigurationSchema>();

  const versions = methods.getValues("versions");

  const uniqueAddresses =
    getUniqueAddressesFromOrderItemConfigurationForm(versions);

  const totalQuantity = versions.reduce((sum, version) => {
    if (version.isDeleted) {
      return sum;
    }
    const dispatchSum = version.dispatches.reduce(
      (s, d) => s + (Number.isNaN(d.quantity) ? 0 : d.quantity || 0),
      0
    );
    return sum + dispatchSum;
  }, 0);

  const addressesAreaRef = useRef<HTMLDivElement>(null);
  const isAddressesScrollable = useIsHorizontallyScrollable(addressesAreaRef, [
    uniqueAddresses,
  ]);

  const handlePressEditVersion = (index: number) => {
    if (onPressEditVersion) {
      onPressEditVersion(`tab_${index}`);
    }
  };

  const handleAddVersion = () => {
    const {
      dispatches,
      name: _name,
      id: _id,
      ...rest
    } = versions.filter((v) => !v.isDeleted)[0];

    const newVersion = {
      id: undefined,
      name: `V${versions.length + 1}`,
      fileSignedId: "",
      isDeleted: false,
      blobId: "",
      fileName: "",
      size: undefined,
      url: "",
      dispatches: dispatches.map((dispatch) => {
        const { quantity: _, ...restDispatch } = dispatch;
        return { quantity: 0, ...restDispatch };
      }),
      ...rest,
    };

    methods.setValue("versions", [...versions, newVersion], {
      shouldDirty: true,
    });
  };

  const handleInputQuantityChange = () => {
    methods.setValue(
      "totalQuantity",
      methods.watch("versions").reduce((sum, version) => {
        if (version.isDeleted) {
          return sum;
        }
        const dispatchSum = (version.dispatches ?? []).reduce(
          (s, d) => s + (Number.isNaN(d.quantity) ? 0 : d.quantity || 0),
          0
        );
        return sum + dispatchSum;
      }, 0)
    );
  };

  const handleInputMaxLength = (event: { currentTarget: HTMLInputElement }) => {
    if (
      event.currentTarget.value.length > MAX_VERSION_PER_ADDRESS_QUANTITY_LENGHT
    ) {
      event.currentTarget.value = event.currentTarget.value.slice(
        0,
        MAX_VERSION_PER_ADDRESS_QUANTITY_LENGHT
      );
    }
  };

  return (
    <>
      {/* Table Versions */}
      <div className={classNames}>
        {/* Version Column */}
        <div className="min-w-min max-w-fit border-r-bw-small border-outlined-border">
          <div className="flex h-si-xl items-center self-stretch rounded-tl-lg bg-surface-second px-sp-md">
            <KdsText size="subtitle-xs" color="text-bodyvariant">
              {t("tableVersions.headerVersion")}
            </KdsText>
          </div>
          <div className="flex h-8 items-center self-stretch border-y-bw-small border-outlined-border bg-surface-third px-sp-md">
            <KdsText
              size="caption-sm"
              color="text-bodyvariant"
              truncate
              dataCy="tableVersionTotalQuantity"
            >
              {t("tableVersions.totalQuantity", {
                quantity: totalQuantity,
              })}
            </KdsText>
          </div>

          {versions.map((version, index) => {
            if (version.isDeleted) {
              return null;
            }
            return (
              <div
                key={`${version.name}_${index}`}
                className="flex h-si-xl items-center gap-2 self-stretch border-b-bw-small border-outlined-border bg-white px-sp-md"
              >
                <div className="line-clamp-1">
                  <KdsText>{version.name}</KdsText>
                </div>
                {!!onExpand && (
                  <KdsButtonIcon
                    size="small"
                    icon={RiEditLine}
                    variant="ghost"
                    onPress={() => handlePressEditVersion(index)}
                  />
                )}
              </div>
            );
          })}
          {!!onExpand && (
            <div className="flex h-si-xl items-center justify-start self-stretch bg-white pr-sp-md">
              <KdsButton
                size="small"
                variant="ghost"
                iconLeft={RiAddCircleFill}
                disabled={!isModificationRequest && versions.some((v) => !v.id)}
                onPress={handleAddVersion}
                dataCy="tableVersionAddVersion"
              >
                {t("tableVersions.addVersion")}
              </KdsButton>
            </div>
          )}
        </div>

        {/* Addresses Columns */}
        <div className="flex w-max overflow-auto">
          {/* Empty column to manage right align */}
          <div className="min-w-0 flex-1">
            <div className="h-si-xl bg-surface-second px-sp-md" />
            <div className="h-8 border-y-bw-small border-outlined-border bg-surface-third px-sp-md" />
            {versions.map((version, index) => {
              if (version.isDeleted) {
                return null;
              }

              return (
                <div
                  key={`${version.name}_${index}`}
                  className="h-si-xl border-b-bw-small border-outlined-border bg-white px-sp-md"
                />
              );
            })}
            {!!onExpand && <div className="h-si-xl bg-white px-sp-md" />}
          </div>

          <div className="flex flex-col max-w-full">
            <div ref={addressesAreaRef} className="flex overflow-auto  w-auto">
              {/* Addresses  */}
              {uniqueAddresses?.map((address, index) => (
                <div
                  key={address.address.name}
                  className="w-auto max-w-[200px]  last:rounded-tr-lg"
                >
                  <div className="flex h-si-xl items-center justify-end self-stretch bg-surface-second px-sp-md">
                    <KdsTooltip alt={address.address.name} placement="top">
                      <KdsFocusableElement className="max-w-full min-w-[80px] ">
                        <KdsText
                          size="subtitle-xs"
                          color="text-bodyvariant"
                          align="right"
                          truncate
                        >
                          {address.address.name}
                        </KdsText>
                      </KdsFocusableElement>
                    </KdsTooltip>
                  </div>
                  <div className="flex h-8 items-center justify-end self-stretch border-y-bw-small border-outlined-border bg-surface-third px-sp-md">
                    <KdsText
                      size="caption-sm"
                      color="text-bodyvariant"
                      dataCy={`tableVersionAddress${index + 1}TotalQuantity`}
                    >
                      {address.quantity}
                    </KdsText>
                  </div>

                  {versions.map((version, indexVersion) => {
                    if (version.isDeleted) {
                      return null;
                    }

                    const registerQuantityField = methods.register(
                      `versions.${indexVersion}.dispatches.${index}.quantity`,
                      {
                        valueAsNumber: true,
                        onChange: handleInputQuantityChange,
                        onBlur: (e) => {
                          const val = e.target.value;
                          if (val === "" || Number(val) < 0) {
                            methods.setValue(
                              `versions.${indexVersion}.dispatches.${index}.quantity`,
                              0
                            );
                          }
                        },
                      }
                    );

                    return (
                      <div
                        key={`${version.name}_${indexVersion}`}
                        className="flex h-si-xl items-center justify-end border-b-bw-small border-outlined-border bg-white px-sp-md"
                      >
                        <KdsInput
                          size="xs"
                          min={0}
                          type="number"
                          onInput={handleInputMaxLength}
                          dataCy={`tableVersionAddress${index + 1}Version${
                            indexVersion + 1
                          }Quantity`}
                          {...registerQuantityField}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="relative flex h-si-xl flex-1 items-center justify-end self-stretch bg-white px-sp-md">
              {!!onExpand && isAddressesScrollable && (
                <KdsButton
                  size="small"
                  variant="outlined"
                  color="neutral"
                  iconLeft={RiExpandDiagonal2Line}
                  onPress={onExpand}
                >
                  {t("buttons.expand")}
                </KdsButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
